import { wrapRequest } from '@applandeo/api-wrapper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ALERT_VARIANT, useAlertMessages } from '@/components/Global/AlertMessages/AlertMessagesProvider';
import { getAllApproversInOrganization, getUserApprovers } from '@/dataExchange/services/newRequest.service';
import { ExtendedUserData } from '@/domain/common';
import { UserApproverResponseDto } from '@/domain/newRequest';
import useLoggedInUserData from '@/hooks/useUserData/useLoggedInUserData';
import { useSentryActions } from '@/sentry/hooks/useSentryActions';
import { SENTRY_ACTIONS } from '@/sentry/sentryActions';

const useApproversLogic = (userId?: string) => {
    const getUserApproversWrapper = wrapRequest(getUserApprovers);
    const getAllApproversDetailsWrapper = wrapRequest(getAllApproversInOrganization);

    const { t } = useTranslation();
    const { createAlert } = useAlertMessages();
    const { credentialId } = useLoggedInUserData();
    const { handleSentryAction } = useSentryActions();

    const [isLoading, setIsLoading] = useState(true);
    const [allApprovers, setAllApprovers] = useState<ExtendedUserData[]>();
    const [userApprovers, setUserApprovers] = useState<UserApproverResponseDto[]>();

    const getUserApproversIds = useCallback(() => {
        if (!credentialId && !userId) return;

        getUserApproversWrapper
            .fetch(userId || credentialId)
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_to_get_approvers'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_USER_APPROVERS);
            })
            .onSuccess(response => {
                setUserApprovers(response);
            });
    }, [createAlert, getUserApproversWrapper, t, credentialId, userId]);

    const getAllApproversDetails = useCallback(() => {
        getAllApproversDetailsWrapper
            .fetch()
            .onAnyError(error => {
                createAlert(ALERT_VARIANT.error, t('newRequest_alerts_failed_to_get_approvers'));
                handleSentryAction(error, SENTRY_ACTIONS.ERROR_GET_ALL_APPROVERS_DETAILS);
            })
            .onSuccess(response => {
                setAllApprovers(response);
                setIsLoading(false);
            });
    }, [createAlert, getAllApproversDetailsWrapper, t]);
    useEffect(getAllApproversDetails, []);
    useEffect(getUserApproversIds, [credentialId, userId]);

    const userApproversWithDetails = useMemo(
        () =>
            userApprovers?.map(userApprover => {
                const approverDetails = allApprovers?.find(
                    ({ credentialId }) => credentialId === userApprover?.managerId
                );
                return { ...approverDetails, pairedId: userApprover?._id };
            }),
        [allApprovers, userApprovers]
    );

    return {
        approvers: userApproversWithDetails,
        isNoUserApprovers: !userApproversWithDetails?.length,
        allApprovers,
        isLoading,
        getUserApproversIds,
    };
};

export default useApproversLogic;
